import { DISPLAY_CURRENCIES, type DisplayCurrency } from '@/lib/currency/map'
import { currencyHref, type CurrencyLabels } from '@/lib/currency/picker'
import { isDefaultLocaleHomepage } from '@/i18n/homepage-href'
import { isLocaleFreeSurface, needsNoLocalePrefix } from '@/i18n/locale-prefix'
import type { Locale } from '@/i18n/locales'

import type { CurrencyOption } from './currency-menu'

/**
 * The address the reader is actually on, from the locale-less path the navigation layer
 * reports plus the active locale.
 *
 * Every option addresses **this same page**, so the path has to be the served one rather
 * than a rebuilt approximation: a locale-free surface carries no prefix, and the default
 * locale's homepage is the bare root. Both carve-outs are read from the helpers that
 * decide them for rendered links, so the picker cannot disagree with `Link` about where
 * the reader is.
 */
function currentPagePath(locale: Locale, pathname: string): string {
    if (isDefaultLocaleHomepage(pathname, locale)) {
        return '/'
    }
    return needsNoLocalePrefix(pathname) ? pathname : `/${locale}${pathname}`
}

/**
 * The picker's options for a reader on `pathname`: every currency this app displays,
 * each addressing the same page stated in it, alphabetically by code as the legacy
 * header orders its columns.
 *
 * `selected` is the currency the page's URL asked for — which is what the control marks,
 * and is the rendered currency in every case but one: a currency whose rate could not be
 * obtained renders canonical amounts while the URL still names it, and the picker then
 * agrees with the URL rather than with the prices.
 */
export function buildCurrencyOptions(
    pathname: string,
    search: string,
    locale: Locale,
    selected: DisplayCurrency,
    labels: CurrencyLabels,
): CurrencyOption[] {
    // A locale-free surface has no locale, so no default to stand a currency against and
    // no page whose prices a choice would change — the country picker is absent there for
    // the matching reason, that the page has no other country's copy to offer.
    if (isLocaleFreeSurface(pathname)) {
        return []
    }

    const path = currentPagePath(locale, pathname)

    return [...DISPLAY_CURRENCIES].sort().map((currency) => ({
        currency,
        ...labels[currency],
        url: currencyHref(path, search, currency, locale),
        isActive: currency === selected,
    }))
}
